import React, { useState, forwardRef } from 'react'
import { Checkbox } from '../../../components'

// Spreadsheet-style bulk editor for the guest list: one row per selected guest,
// one column per field the user chooses to edit, every cell individually
// editable — so e.g. a golf event can enter a *different* handicap for each of
// ten guests in one pass. Each column header carries a "fyll alla" control to
// stamp the whole column at once (the same-value-for-everyone case).
//
// Unlike a `/bulk` (one field, one value) call, per-guest values can differ, so
// submit hands the parent a [{ uuid, payload }] list and the parent applies one
// `update` per guest (custom_data merges server-side). Only guests whose values
// actually changed are included.
//
// `fields` is [{ field, key, label, type, options?, isCustom }]:
//   - field:    'event_guest_response' | 'event_guest_custom_data.<key>' (the write target)
//   - key:      response → 'event_guest_response'; custom → the JSONB key
//   - type:     'select' | 'checkbox' | 'number' | 'text'
//   - isCustom: false for the built-in response column, true for a custom field
const _GuestBulkEditForm = ({ data, fields, submitForm, loading, setLoading }, ref) => {

  const currentValue = (guest, f) => {
    if (f.isCustom) {
      const v = guest.event_guest_custom_data?.[f.key]
      if (f.type === 'checkbox') return !!v
      return v ?? ''
    }
    return guest.event_guest_response ?? ''
  }

  // values[guestUuid][field] = edited value, seeded from each guest's current value
  const [values, setValues] = useState(() => {
    const seed = {}
    data.forEach(g => {
      seed[g.event_guest_uuid] = {}
      fields.forEach(f => { seed[g.event_guest_uuid][f.field] = currentValue(g, f) })
    })
    return seed
  })

  // Which fields are shown as columns (default: all). Lets the user narrow a
  // wide grid down to just the field(s) they came to edit.
  const [activeCols, setActiveCols] = useState(() => fields.map(f => f.field))
  const [fillValues, setFillValues] = useState({})
  const [error, setError] = useState(null)

  const cols = fields.filter(f => activeCols.includes(f.field))

  const toggleCol = (field) => {
    setActiveCols(prev => prev.includes(field) ? prev.filter(x => x !== field) : [...prev, field])
  }

  const setCell = (uuid, field, value) => {
    setValues(prev => ({ ...prev, [uuid]: { ...prev[uuid], [field]: value } }))
    setError(null)
  }

  const fillAll = (f) => {
    const value = f.type === 'checkbox' ? !!fillValues[f.field] : (fillValues[f.field] ?? '')
    setValues(prev => {
      const next = { ...prev }
      data.forEach(g => { next[g.event_guest_uuid] = { ...next[g.event_guest_uuid], [f.field]: value } })
      return next
    })
    setError(null)
  }

  // Compare edited vs. current, treating '' and boolean sensibly, so an
  // untouched cell never produces a write.
  const isChanged = (guest, f) => {
    const now = values[guest.event_guest_uuid]?.[f.field]
    const before = currentValue(guest, f)
    if (f.type === 'checkbox') return !!now !== !!before
    return String(now ?? '') !== String(before ?? '')
  }

  const onSubmit = (event) => {
    event.preventDefault()

    if (!cols.length) { setError('Välj minst ett fält att redigera'); return }

    const changes = []
    data.forEach(guest => {
      const payload = {}
      let custom = null
      cols.forEach(f => {
        if (!isChanged(guest, f)) return
        const raw = values[guest.event_guest_uuid][f.field]
        if (f.isCustom) {
          if (!custom) custom = {}
          // '' clears the key; booleans/numbers/strings pass through — the API
          // coerces by field_type and merges into the guest's existing object.
          custom[f.key] = f.type === 'checkbox' ? !!raw : (raw === '' ? null : raw)
        } else {
          payload.event_guest_response = raw === '' ? null : raw
        }
      })
      if (custom) payload.event_guest_custom_data = custom
      if (Object.keys(payload).length) changes.push({ uuid: guest.event_guest_uuid, payload })
    })

    if (!changes.length) { setError('Inga ändringar att spara'); return }

    setLoading(true)
    submitForm(changes)
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  const renderCell = (guest, f) => {
    const uuid = guest.event_guest_uuid
    const value = values[uuid]?.[f.field]
    if (f.type === 'checkbox') {
      return <Checkbox name={`${uuid}-${f.field}`} checked={!!value} onChange={() => setCell(uuid, f.field, !value)} disabled={loading} />
    }
    if (f.type === 'select') {
      return (
        <select className="ev-bulk-input" value={value ?? ''} disabled={loading} onChange={e => setCell(uuid, f.field, e.target.value)}>
          {(f.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )
    }
    return (
      <input
        className="ev-bulk-input"
        type={f.type === 'number' ? 'number' : 'text'}
        value={value ?? ''}
        disabled={loading}
        onChange={e => setCell(uuid, f.field, e.target.value)}
      />
    )
  }

  const renderFillControl = (f) => {
    if (f.type === 'checkbox') {
      return (
        <div className="ev-bulk-fill">
          <Checkbox name={`fill-${f.field}`} checked={!!fillValues[f.field]} onChange={() => setFillValues(p => ({ ...p, [f.field]: !p[f.field] }))} disabled={loading} label="alla" />
          <button type="button" className="ev-bulk-fill-btn" onClick={() => fillAll(f)} disabled={loading}>Fyll</button>
        </div>
      )
    }
    if (f.type === 'select') {
      return (
        <div className="ev-bulk-fill">
          <select
            className="ev-bulk-input"
            value={fillValues[f.field] ?? ''}
            disabled={loading}
            onChange={e => { const v = e.target.value; setFillValues(p => ({ ...p, [f.field]: v })); }}
          >
            <option value="">Fyll alla…</option>
            {(f.options || []).filter(o => o.value !== '').map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button type="button" className="ev-bulk-fill-btn" onClick={() => fillAll(f)} disabled={loading}>Fyll</button>
        </div>
      )
    }
    return (
      <div className="ev-bulk-fill">
        <input
          className="ev-bulk-input"
          type={f.type === 'number' ? 'number' : 'text'}
          placeholder="Fyll alla…"
          value={fillValues[f.field] ?? ''}
          disabled={loading}
          onChange={e => setFillValues(p => ({ ...p, [f.field]: e.target.value }))}
        />
        <button type="button" className="ev-bulk-fill-btn" onClick={() => fillAll(f)} disabled={loading}>Fyll</button>
      </div>
    )
  }

  return (
    <div className="form">
      {error && <div className="form-error">{error}</div>}

      <p className="ev-optional" style={{ marginBottom: 12 }}>
        Redigera {data.length} gäst{data.length > 1 ? 'er' : ''}. Skriv ett värde per gäst, eller använd <strong>Fyll</strong> för att sätta samma värde på alla.
      </p>

      <div className="ev-bulk-fieldpicker">
        {fields.map(f => (
          <button
            key={f.field}
            type="button"
            className={'ev-bulk-fieldchip' + (activeCols.includes(f.field) ? ' on' : '')}
            onClick={() => toggleCol(f.field)}
            disabled={loading}
          >{f.label}</button>
        ))}
      </div>

      <form onSubmit={onSubmit} ref={ref}>
        {cols.length === 0
          ? <p className="ev-optional">Inga fält valda.</p>
          : (
            <div className="ev-bulk-grid-scroll">
              <table className="ev-bulk-grid">
                <thead>
                  <tr>
                    <th className="ev-bulk-namehead">Gäst</th>
                    {cols.map(f => (
                      <th key={f.field}>
                        <div className="ev-bulk-collabel">{f.label}</div>
                        {renderFillControl(f)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map(guest => (
                    <tr key={guest.event_guest_uuid}>
                      <td className="ev-bulk-name">{guest.event_guest_name}</td>
                      {cols.map(f => <td key={f.field}>{renderCell(guest, f)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </form>
    </div>
  )
}

export const GuestBulkEditForm = forwardRef(_GuestBulkEditForm)
